import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { alpha, Typography } from '@mui/material';
import { UserRoundCheck } from 'lucide-react';
import { CounterpartyIcon } from 'src/components/icons/CounterpartyIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { PlaceholderIcon } from 'src/components/icons/PlaceholderIcon';
import { QueuedIcon } from 'src/components/icons/QueuedIcon';
import { SmallSigningIcon } from 'src/components/icons/SmallSigningIcon';
import { WorkflowOwnerIcon } from 'src/components/icons/WorkflowOwnerIcon';
import { LAUNCHER_PLACEHOLDER_TEXT } from 'src/components/workflow/workflow-helpers';
import { Apollo } from 'src/gql/generated';
import type { AccountSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const ASSIGNEE_CHIP_TEXT = '#3F35A8';
const ASSIGNEE_CHIP_BACKGROUND = 'rgba(91, 79, 217, 0.12)';

const useStyles = makeStyles()(theme => ({
  chips: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

type WorkflowTaskAction = Pick<Apollo.Task, '__typename' | 'id'>;
type WorkflowRecipientAction = Pick<Apollo.WorkflowRecipient, '__typename' | 'id' | 'type'>;
type WorkflowAction = WorkflowTaskAction | WorkflowRecipientAction;

export interface WorkflowPermissionSubsetWithActions extends Pick<
  Apollo.WorkflowPermission,
  'id' | 'isExternal' | 'timeliness' | '__typename'
> {
  account: AccountSubset | null;
  actions: WorkflowAction[];
  roleName: string | null;
  isAssignee?: boolean;
  isLauncher?: boolean;
}

export function WorkflowCollaboratorsChips({
  permissionLabels,
}: {
  permissionLabels: WorkflowPermissionLabel[];
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.chips}>
      {permissionLabels.map((label, labelIndex) => (
        <WorkflowCollaboratorsChip key={labelIndex} label={label} />
      ))}
    </div>
  );
}

interface ChipStyleProps {
  backgroundColor: string;
  textColor: string;
}

const useWorkflowCollaboratorsChipStyles = makeStyles<ChipStyleProps>()(
  (theme, { backgroundColor, textColor }) => ({
    chip: {
      alignItems: 'center',
      backgroundColor,
      borderRadius: 8,
      display: 'flex',
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.25, 0.5),
    },
    chipText: {
      color: textColor,
      fontSize: 10,
      fontWeight: 600,
    },
  })
);

export function WorkflowCollaboratorsChip({ label }: { label: WorkflowPermissionLabel }) {
  const { classes } = useWorkflowCollaboratorsChipStyles({
    backgroundColor: label.backgroundColor,
    textColor: label.textColor,
  });

  return (
    <div className={classes.chip}>
      <WorkflowLabelIcon type={label.type} />
      <Typography className={classes.chipText}>{label.labelText}</Typography>
    </div>
  );
}

type WorkflowLabelType =
  | 'assignee'
  | 'counterparty'
  | 'owner'
  | 'placeholder'
  | 'queued'
  | 'signer';
type WorkflowPermissionLabel = {
  backgroundColor: string;
  labelText: string;
  textColor: string;
  type: WorkflowLabelType;
};

export function WorkflowLabelIcon({ type }: { type: WorkflowLabelType }): JSX.Element {
  switch (type) {
    case 'assignee':
      return <UserRoundCheck color={ASSIGNEE_CHIP_TEXT} size={12} />;
    case 'counterparty':
      return <CounterpartyIcon />;
    case 'owner':
      return <WorkflowOwnerIcon />;
    case 'placeholder':
      return <PlaceholderIcon />;
    case 'queued':
      return <QueuedIcon />;
    case 'signer':
      return <SmallSigningIcon />;
  }
}

export function useGetWorkflowPermissionLabels(
  workflowId: UUID,
  permission: WorkflowPermissionSubsetWithActions
) {
  const { workflow } = useGetWorkflowWithOwner(workflowId);

  return useMemo(() => {
    const labels: WorkflowPermissionLabel[] = [];
    const isOwner = workflow?.owner.id === permission.account?.id;
    const isPlaceholder = Boolean(
      permission.isLauncher || (permission.roleName && !permission.account)
    );
    const isQueued = permission.timeliness === Apollo.WorkflowPermissionTimeliness.Queued;
    const isSigner = permission.actions.some(
      a => a.__typename === 'WorkflowRecipient' && a.type == Apollo.WorkflowRecipientType.Signer
    );

    /**
     * Order of if statements matters in terms of rendering.  Keep things like owner, signer at the top.
     */
    if (isOwner)
      labels.push({
        backgroundColor: 'rgba(115, 230, 207, 0.3)',
        labelText: 'Owner',
        textColor: '#085D4C',
        type: 'owner',
      });

    if (isSigner)
      labels.push({
        backgroundColor: 'rgba(115, 165, 230, 0.30)',
        labelText: 'Signer',
        textColor: '#074595',
        type: 'signer',
      });

    if (permission.isAssignee)
      labels.push({
        backgroundColor: ASSIGNEE_CHIP_BACKGROUND,
        labelText: 'Assignee',
        textColor: ASSIGNEE_CHIP_TEXT,
        type: 'assignee',
      });

    if (isPlaceholder)
      labels.push({
        backgroundColor: 'rgba(235, 236, 246, 1)',
        labelText: permission.isLauncher ? LAUNCHER_PLACEHOLDER_TEXT : 'Placeholder',
        textColor: '#3C44A2',
        type: 'placeholder',
      });

    if (isQueued)
      labels.push({
        backgroundColor: alpha(IconColors.DarkGrey, 0.15),
        labelText: 'Queued',
        textColor: IconColors.DarkGrey,
        type: 'queued',
      });

    if (permission.isExternal)
      labels.push({
        backgroundColor: 'rgba(233, 211, 123, 0.30)',
        labelText: 'Counterparty',
        textColor: '#6E4B28',
        type: 'counterparty',
      });

    return labels;
  }, [permission, workflow?.owner.id]);
}

void gql`
  query GetWorkflowWithOwner($workflowId: UUID!) {
    workflow(workflowId: $workflowId) {
      __typename
      id
      owner {
        __typename
        id
      }
    }
  }
`;

export function useGetWorkflowWithOwner(workflowId: UUID) {
  const { data, loading } = Apollo.useGetWorkflowWithOwnerQuery({ variables: { workflowId } });
  return { loading, workflow: data?.workflow ?? null };
}
